// UPDATED AdminStudentsListing with server-side pagination and search
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '../../../../../svg/DeleteIcon';
import ToggleSwitch from '../../../../components/common/ToggleSwitch';
import { showSuccessToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import { ConfirmActionModal } from '../../../../components/common/Modal/ConfirmActionModal';
import {
    useBlockUserMutation,
    useDeleteUserMutation,
    useUnblockUserMutation,
    useLazyGetAllUsersQuery,
} from '../../../../../store/services/admin/UserService';
import { getErrorMessage } from '../../../../../utils/utils';
import { Table } from '../../../../components/Sessions/Table';
import Pagination from '../../../../components/Dashboard/Pagination';

export const AdminStudentsListing = () => {
    const navigate = useNavigate();
    const [studentsData, setStudentsData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState(null);
    const [pageSize] = useState(5);

    const [triggerGetAllUsers, { data, isLoading, error }] = useLazyGetAllUsersQuery();

    const fetchData = () => {
        triggerGetAllUsers({ pageNumber: currentPage, pageSize, search: searchTerm });
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, searchTerm]);

    useEffect(() => {
        if (data?.data) {
            setStudentsData(data.data?.users);
            setTotalCount(data.data.totalCount);
        }
    }, [data]);

    const [deleteUser] = useDeleteUserMutation();

    const confirmDeleteStudent = async () => {
        const response = await deleteUser({ id: selectedStudentId });
        if (response?.data?.succeeded) {
            showSuccessToast(response.data.data?.message);
            fetchData();
            setShowDeleteModal(false);
        } else {
            getErrorMessage(response?.error?.data?.errors[0]);
            setShowDeleteModal(false);
        }
    };

    const handleStudentDeleteClick = (event, id) => {
        event.stopPropagation();
        setSelectedStudentId(id);
        setShowDeleteModal(true);
    };

    const Actions = ({ id }) => (
        <div className="d-flex gap-2">
            <div onClick={(e) => handleStudentDeleteClick(e, id)}>
                <DeleteIcon fill="#ff0000" width={18} height={18} cursor="pointer" />
            </div>
        </div>
    );

    const Status = ({ student }) => {
        const [blockUser] = useBlockUserMutation();
        const [unblockUser] = useUnblockUserMutation();

        const handleStatusToggle = async (newStatus) => {
            newStatus ? await unblockUser({ id: student.id }) : await blockUser({ id: student.id });
            showSuccessToast(`Status for ${student.name} changed to ${newStatus ? 'Active' : 'Inactive'}`);
            //fetchData();
        };

        return <ToggleSwitch initialState={student.isActive} onToggle={handleStatusToggle} />;
    };

    const columns = [
        {
            header: 'User ID',
            accessor: 'id',
        },
        {
            header: 'Name',
            accessor: 'name',
            render: (student) => (
                <span
                    onClick={() => navigate(`/student/${student.id}`)}
                    className="text-primary fw-semibold text-decoration-underline cursor-pointer w-auto"
                >
                    {student.name}
                </span>
            ),
        },
        { header: 'Email', accessor: 'email' },
        { 
            header: 'Age', 
            accessor: 'goal.ageGroup',
            render: (student) => student.goal?.ageGroup || 'N/A',
        },
        {
            header: 'Interest',
            accessor: 'interested',
        },
        {
            header: 'Purpose',
            accessor: 'purposeLanguage',
        },
        {
            header: 'Education Level',
            accessor: 'goal.education',
            render: (student) => student.goal?.education || 'N/A',
        },
        {
            header: 'Learning Reason',
            accessor: 'goal.learningReason',
            render: (student) => student.goal?.learningReason || 'N/A',
        },
        {
            header: 'Learning Time',
            accessor: 'goal.learningTime',
            render: (student) => `${student.goal?.learningTime || 0} mins`,
        },
        {
            header: 'Timezone',
            accessor: 'goal.timeZone',
            render: (student) => student.goal?.timeZone || 'N/A',
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (student) => <Status student={student} />,
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: (student) => <Actions id={student.id} />,
        },
    ];

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error loading users.</div>;

    return (
        <div className="bg-white">
            <div className="page-title flex-wrap mb-0">
                <div className="input-group search-area mb-md-0 ml-3">
                    <input
                        type="text"
                        className="form-control ps-4"
                        placeholder="Search here..."
                        value={searchTerm}
                        onChange={(e) => {
                            setCurrentPage(1);
                            setSearchTerm(e.target.value);
                        }}
                    />
                </div>
            </div>

            <Table
                data={studentsData}
                columns={columns}
                pageSize={pageSize}
                filterConfig={[]}
                buttonConfig={[]}
                searchableColumns={[]} // moved to parent
            />
            <Pagination currentPage={currentPage} totalCount={totalCount}
            pageSize={pageSize} onPageChange={setCurrentPage} />

            <ConfirmActionModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteStudent}
                title="Delete Student"
                message="Are you sure you want to delete this student? This action cannot be undone."
                confirmText="Delete"
            />
        </div>
    );
};